import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import TambaLogo from "@/components/TambaLogo";
import ThemeToggle from "@/components/ThemeToggle";
import { 
  MessageCircle, 
  PieChart, 
  Target, 
  Settings, 
  LogOut,
  DollarSign,
  ArrowRight,
  Wallet,
  Gift,
  TrendingUp,
  Briefcase,
  CreditCard,
  Sparkles,
  User
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem("tamba_user");
    if (!storedUser) {
      navigate("/auth");
      return;
    }
    
    const userData = JSON.parse(storedUser);
    setUser(userData);
    fetchDashboardData(userData.id);
  }, [navigate]);

  const fetchDashboardData = async (userId) => {
    try {
      const response = await axios.get(`${API}/dashboard/${userId}`);
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("tamba_user");
    toast.success("Logged out successfully");
    navigate("/");
  };

  const navItems = [
    { icon: MessageCircle, label: "AI Advisor", path: "/chat", color: "text-tamba-blue bg-tamba-blue/10" },
    { icon: PieChart, label: "Budget", path: "/budget", color: "text-tamba-coral bg-tamba-coral/10" },
    { icon: Target, label: "Goals", path: "/goals", color: "text-tamba-teal bg-tamba-teal/10" },
    { icon: TrendingUp, label: "Invest", path: "/invest", color: "text-green-600 bg-green-100" },
    { icon: Sparkles, label: "Projection", path: "/projection", color: "text-purple-600 bg-purple-100" }
  ];

  const categories = dashboardData?.expenses?.by_category || {};

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse">
          <TambaLogo className="h-16 w-16" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border/30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <TambaLogo className="h-10 w-auto" />
          </div>
          
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/settings")}
              data-testid="settings-btn"
            >
              <Settings className="h-5 w-5" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 pb-24">
        {/* Demo Mode Banner */}
        {user?.id === "b118ce76-6cd4-486f-b1fa-29c2f71c50e6" && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-gradient-to-r from-tamba-teal/20 to-tamba-blue/20 border border-tamba-teal/30 rounded-2xl"
          >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-tamba-teal/20 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-tamba-teal" />
                </div>
                <div>
                  <p className="font-semibold text-sm">You're exploring the demo</p>
                  <p className="text-xs text-muted-foreground">This is sample data for Alex Johnson's account</p>
                </div>
              </div>
              <Button
                onClick={() => {
                  localStorage.removeItem("tamba_user");
                  navigate("/auth");
                }}
                className="rounded-full bg-tamba-blue hover:bg-tamba-blue/90"
                size="sm"
                data-testid="create-account-btn"
              >
                Create Your Account
              </Button>
            </div>
          </motion.div>
        )}

        {/* Greeting with Profile Link */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-1">
                Hello, {user?.name || "Wealth Builder"} 👋
              </h1>
              <p className="text-muted-foreground">Here's your financial overview</p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate("/profile")}
              className="rounded-full hidden sm:flex"
              data-testid="profile-btn"
            >
              <User className="h-4 w-4 mr-2" />
              My Profile
            </Button>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <div className="grid grid-cols-5 gap-2 sm:gap-3 mb-8">
          {navItems.map((item, index) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Button
                variant="outline"
                onClick={() => navigate(item.path)}
                className={`w-full h-auto flex-col gap-1 sm:gap-2 py-3 sm:py-4 rounded-xl border-border/50 hover:border-tamba-blue/50 hover:shadow-md transition-all`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-').replace('&', '')}-btn`}
              >
                <div className={`p-2 rounded-lg ${item.color}`}>
                  <item.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                </div>
                <span className="text-[10px] sm:text-xs font-medium truncate">{item.label}</span>
              </Button>
            </motion.div>
          ))}
        </div>

        {/* Summary Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Net Worth */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="border-border/50 hover:shadow-lg hover:border-tamba-teal/30 transition-all bg-gradient-to-br from-tamba-teal/5 to-transparent">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-tamba-teal/10 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-tamba-teal" />
                  </div>
                  <span className="text-xs text-green-600 font-medium">+12.4%</span>
                </div>
                <p className="text-sm text-muted-foreground mb-1">Net Worth</p>
                <p className="font-heading text-2xl font-bold">
                  ${(dashboardData?.summary?.net_worth || 0).toLocaleString()}
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Total Spent */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <Card className="border-border/50 hover:shadow-lg hover:border-tamba-coral/30 transition-all">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-tamba-coral/10 flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-tamba-coral" />
                  </div>
                  <span className="text-xs text-muted-foreground">This month</span>
                </div>
                <p className="text-sm text-muted-foreground mb-1">Total Spent</p>
                <p className="font-heading text-2xl font-bold">
                  ${(dashboardData?.summary?.total_expenses || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Goals Progress */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="border-border/50 hover:shadow-lg hover:border-tamba-blue/30 transition-all">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-tamba-blue/10 flex items-center justify-center">
                    <Target className="h-5 w-5 text-tamba-blue" />
                  </div>
                  <span className="text-xs text-muted-foreground">{dashboardData?.quick_stats?.goals_count || 0} goals</span>
                </div>
                <p className="text-sm text-muted-foreground mb-1">Goals Progress</p>
                <div className="flex items-baseline gap-1">
                  <p className="font-heading text-2xl font-bold">
                    {dashboardData?.summary?.goals_progress?.percentage || 0}%
                  </p>
                  <span className="text-sm text-muted-foreground">complete</span>
                </div>
                <Progress value={dashboardData?.summary?.goals_progress?.percentage || 0} className="h-1.5 mt-2" />
              </CardContent>
            </Card>
          </motion.div>

          {/* Subscription Status */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <Card className="border-border/50 hover:shadow-lg hover:border-tamba-yellow/30 transition-all">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-tamba-yellow/10 flex items-center justify-center">
                    <Wallet className="h-5 w-5 text-tamba-yellow" />
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    user?.subscription_status === "premium" 
                      ? "bg-tamba-yellow/20 text-amber-700 font-medium" 
                      : "bg-muted text-muted-foreground"
                  }`}>
                    {user?.subscription_status === "premium" ? "Premium" : "Free"}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-2">Account</p>
                {user?.subscription_status !== "premium" ? (
                  <Button 
                    size="sm" 
                    onClick={() => navigate("/pricing")}
                    className="rounded-full bg-tamba-blue hover:bg-tamba-blue/90"
                    data-testid="upgrade-btn"
                  >
                    Upgrade Now
                  </Button>
                ) : (
                  <p className="font-heading text-lg font-bold text-tamba-teal">Active</p>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Goals Section */}
        {dashboardData?.goals && dashboardData.goals.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-semibold">Your Goals</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate("/goals")} className="text-tamba-blue">
                View All <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {dashboardData.goals.slice(0, 4).map((goal) => {
                const progress = goal.target_amount > 0 ? (goal.current_amount / goal.target_amount) * 100 : 0;
                return (
                  <Card key={goal.id} className="border-border/50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-sm">{goal.name}</p>
                        <span className="text-xs text-muted-foreground">{Math.round(progress)}%</span>
                      </div>
                      <Progress value={progress} className="h-2 mb-2" />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>${goal.current_amount?.toLocaleString()}</span>
                        <span>${goal.target_amount?.toLocaleString()}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Portfolio Holdings */}
        {dashboardData?.portfolio_holdings && dashboardData.portfolio_holdings.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-semibold">Investment Portfolio</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate("/invest")} className="text-tamba-teal">
                View All <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <div className="space-y-3">
                  {dashboardData.portfolio_holdings.map((holding, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-tamba-blue/10 flex items-center justify-center">
                          <Briefcase className="h-4 w-4 text-tamba-blue" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{holding.symbol}</p>
                          <p className="text-xs text-muted-foreground">{holding.name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-sm">${(holding.shares * holding.avg_price).toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">{holding.shares} shares</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Upcoming Bills */}
        {dashboardData?.upcoming_bills && dashboardData.upcoming_bills.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-semibold">Upcoming Bills</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate("/bills")} className="text-tamba-coral">
                View All <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <div className="space-y-3">
                  {dashboardData.upcoming_bills.map((bill, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-tamba-coral/10 flex items-center justify-center">
                          <CreditCard className="h-4 w-4 text-tamba-coral" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{bill.name}</p>
                          <p className="text-xs text-muted-foreground">{bill.category}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-sm">${bill.amount?.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">Due: {bill.due_date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Spending by Category */}
        {Object.keys(categories).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mb-8"
          >
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg flex items-center justify-between">
                  Spending by Category
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigate("/budget")}
                    className="text-sm text-tamba-blue"
                    data-testid="view-budget-btn"
                  >
                    View All
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="space-y-4">
                  {Object.entries(categories).slice(0, 4).map(([category, amount], idx) => {
                    const total = dashboardData?.expenses?.total || 1;
                    const percentage = (amount / total) * 100;
                    const colors = ["bg-tamba-blue", "bg-tamba-yellow", "bg-tamba-coral", "bg-tamba-teal"];
                    return (
                      <div key={category}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">{category}</span>
                          <span className="text-sm text-muted-foreground">${amount.toFixed(2)}</span>
                        </div>
                        <div className="h-2 bg-secondary rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${colors[idx % colors.length]} rounded-full transition-all`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* AI Assistant CTA */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-6"
        >
          <Card className="border-tamba-blue/30 bg-gradient-to-r from-tamba-blue/5 to-tamba-teal/5">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-tamba-blue/20 flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="h-6 w-6 text-tamba-blue" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-lg font-semibold mb-1">Talk to Your AI Advisor</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Get personalized financial advice, budgeting tips, and wealth-building strategies
                  </p>
                  <Button 
                    onClick={() => navigate("/chat")}
                    className="rounded-full bg-tamba-blue hover:bg-tamba-blue/90 tamba-shadow"
                    data-testid="chat-cta-btn"
                  >
                    Start Conversation
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Referral Program CTA */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
        >
          <Card className="border-tamba-yellow/30 bg-gradient-to-r from-tamba-yellow/5 to-tamba-coral/5">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-tamba-yellow/20 flex items-center justify-center flex-shrink-0">
                  <Gift className="h-6 w-6 text-tamba-yellow" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-lg font-semibold mb-1">Refer Friends, Earn Free Months</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Share Tamba with friends. Get 1 month FREE for each friend who subscribes!
                  </p>
                  <Button 
                    onClick={() => navigate("/referral")}
                    className="rounded-full bg-tamba-yellow hover:bg-tamba-yellow/90 text-black"
                    data-testid="referral-cta-btn"
                  >
                    Start Referring
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-background/80 backdrop-blur-xl border-t border-border/50 px-2 py-2 z-50">
        <div className="max-w-lg mx-auto flex items-center justify-around">
          {[
            { icon: PieChart, label: "Budget", path: "/budget", color: "text-tamba-coral" },
            { icon: TrendingUp, label: "Invest", path: "/invest", color: "text-green-600" },
            { icon: MessageCircle, label: "Chat", path: "/chat", color: "text-tamba-blue" },
            { icon: Wallet, label: "Banks", path: "/bank-connection", color: "text-purple-600" },
            { icon: Settings, label: "More", path: "/settings", color: "text-muted-foreground" }
          ].map((item) => (
            <Button
              key={item.label}
              variant="ghost"
              onClick={() => navigate(item.path)}
              className={`flex-col h-auto py-2 px-3 ${item.color} hover:bg-secondary`}
              data-testid={`bottom-nav-${item.label.toLowerCase()}-btn`}
            >
              <item.icon className="h-5 w-5 mb-1" />
              <span className="text-[10px]">{item.label}</span>
            </Button>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default Dashboard;
